// Centralized date/time handling so every timestamp renders in the BROWSER's
// timezone consistently.
//
// Backend convention: timestamps are stored/emitted as UTC ISO-8601, normally
// with a "+00:00" offset. To be safe we treat ANY value that lacks a timezone
// marker as UTC (rather than letting `new Date()` guess it as browser-local),
// so display is identical no matter where the server or the browser sit.

const TZ_RE = /([zZ])$|([+-]\d{2}:?\d{2})$/;
const DATETIME_RE = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/;

/**
 * Parse a backend timestamp (ISO string, ms epoch, or Date) into a Date.
 * A date+time string with no timezone is assumed to be UTC.
 * @returns {Date|null} null when the input is empty/invalid.
 */
export function parseTimestamp(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  let s = String(value).trim();
  if (DATETIME_RE.test(s) && !TZ_RE.test(s)) {
    // Has a time component but no tz → it's UTC; mark it so JS doesn't read it as local.
    s = s.replace(' ', 'T') + 'Z';
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

const pad = (n) => String(n).padStart(2, '0');

/** Date only, in the browser's timezone/locale. e.g. "6/4/2026". */
export function formatDate(value, options) {
  const d = parseTimestamp(value);
  if (!d) return '';
  return d.toLocaleDateString(undefined, options);
}

/** Date + time, in the browser's timezone/locale. */
export function formatDateTime(value, options) {
  const d = parseTimestamp(value);
  if (!d) return '';
  return d.toLocaleString(undefined, options);
}

/** Time only, in the browser's timezone/locale. e.g. "2:30 PM". */
export function formatTime(value, options) {
  const d = parseTimestamp(value);
  if (!d) return '';
  return d.toLocaleTimeString(undefined, options);
}

/**
 * Relative "time ago" label (just now / 5m / 3h / 2d), falling back to an
 * absolute date for anything older than a week.
 */
export function timeAgo(value) {
  const d = parseTimestamp(value);
  if (!d) return '';
  const secs = (Date.now() - d.getTime()) / 1000;
  if (secs < 0) return 'just now';
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 86400 * 7) return `${Math.floor(secs / 86400)}d ago`;
  return formatDate(value, { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Convert a backend UTC timestamp into the value string a
 * <input type="datetime-local"> expects — i.e. the local wall-clock time
 * ("YYYY-MM-DDTHH:mm"), NOT the raw UTC slice.
 */
export function toDatetimeLocalValue(value) {
  const d = parseTimestamp(value);
  if (!d) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Convert a <input type="datetime-local"> value (local wall-clock time) into a
 * UTC ISO string for the backend. The browser-local components are interpreted
 * as local time, then serialized to UTC.
 */
export function datetimeLocalToUTC(localValue) {
  if (!localValue) return null;
  const [datePart, timePart] = String(localValue).split('T');
  if (!datePart || !timePart) return null;
  const [y, m, day] = datePart.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  const d = new Date(y, (m || 1) - 1, day, hh || 0, mm || 0, 0, 0);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

/** "now" as a datetime-local input value (browser-local), for min=/default. */
export function nowDatetimeLocalValue() {
  return toDatetimeLocalValue(new Date());
}
